// src/components/AtivacoesModal.jsx
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Typography, CircularProgress, Tooltip,
  Button, TextField, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';

export default function AtivacoesModal({ open, onClose, licenca, isAdmin = false }) {
  const [ativacoes, setAtivacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desativandoId, setDesativandoId] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const [ativandoId, setAtivandoId] = useState(null);

  // inserir (entrada LIVRE)
  const [openInserir, setOpenInserir] = useState(false);
  const [form, setForm] = useState({ id_computador: '', info_complementar: '' });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open && licenca) {
      setLoading(true);
      api
        .get(`/ativacao_CSHAP?licenca_id=${licenca.id}`)
        .then((res) => {
          setAtivacoes(res.data.ativacoes || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [open, licenca]);

  const handleDesativar = async (id) => {
    setDesativandoId(id);
    try {
      await api.post('/ativacao_CSHAP/desativarAtivacao', { id_ativacao: id });
      setAtivacoes((at) => at.map((a) => (a.id === id ? { ...a, status: 'desativada' } : a)));
    } catch {
      alert('Erro ao desativar ativação.');
    }
    setDesativandoId(null);
  };

  const handleAtivar = async (id) => {
    setAtivandoId(id);
    try {
      await api.post('/ativacao_CSHAP/reativarAtivacao', { id_ativacao: id });
      setAtivacoes((at) => at.map((a) => (a.id === id ? { ...a, status: 'ativa' } : a)));
    } catch {
      alert('Erro ao ativar novamente.');
    }
    setAtivandoId(null);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta ativação? Esta ação não pode ser desfeita.')) return;
    setExcluindoId(id);
    try {
      await api.delete(`/ativacao_CSHAP/${id}`);
      setAtivacoes((at) => at.filter((a) => a.id !== id));
    } catch {
      alert('Erro ao excluir ativação.');
    }
    setExcluindoId(null);
  };

  // ===== Inserção livre =====
  const abrirInserir = () => {
    setForm({ id_computador: '', info_complementar: '' });
    setOpenInserir(true);
  };

  const salvarInserir = async () => {
    const idComputador = (form.id_computador || '').trim(); // única regra: não vazio
    if (!idComputador) {
      alert('Informe o ID do computador (qualquer texto).');
      return;
    }
    setSalvando(true);
    try {
      const body = {
        licenca_id: licenca.id,
        id_computador: idComputador,                 // sem validar/formatar
        info_complementar: form.info_complementar || null,
      };
      const res = await api.post('/ativacao_CSHAP', body);
      const created = res.data?.ativacao || res.data;

      const nova = created || {
        id: Math.random().toString(36).slice(2),
        id_computador: idComputador,
        status: 'ativa',
        data_ativacao: new Date().toISOString(),
        info_complementar: form.info_complementar || null,
      };

      setAtivacoes((prev) => [...prev, nova]);
      setOpenInserir(false);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || 'Falha ao inserir ativação.';
      alert(msg);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 10 }}>
        Ativações: {licenca?.cliente_nome} ({licenca?.chave})
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', right: 48, top: 8 }}>
          {isAdmin && (
            <Tooltip title="Inserir ativação">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={abrirInserir}
                >
                  Inserir
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography align="center" sx={{ py: 3 }}>
            <CircularProgress size={32} />
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID Computador</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Ativação</TableCell>
                <TableCell>Info</TableCell>
                <TableCell align="center">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ativacoes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>Nenhuma ativação encontrada.</TableCell>
                </TableRow>
              )}
              {ativacoes.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.id_computador}</TableCell>
                  <TableCell>
                    <span
                      style={{
                        color:
                          a.status === 'ativa'
                            ? '#43a047'
                            : a.status === 'bloqueada'
                            ? '#f44336'
                            : '#888'
                      }}
                    >
                      {a.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {a.data_ativacao?.substring(0, 16).replace('T', ' ') || '-'}
                  </TableCell>
                  <TableCell>{a.info_complementar || '-'}</TableCell>
                  <TableCell align="center">
                    {a.status === 'ativa' && (
                      <Tooltip title="Desativar">
                        <span>
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() => handleDesativar(a.id)}
                            disabled={desativandoId === a.id}
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {a.status === 'desativada' && (
                      <Tooltip title="Ativar novamente">
                        <span>
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleAtivar(a.id)}
                            disabled={ativandoId === a.id}
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Excluir ativação">
                      <span>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleExcluir(a.id)}
                          disabled={excluindoId === a.id}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      {/* Modal de Inserção (entrada LIVRE) */}
      <Dialog open={openInserir} onClose={() => setOpenInserir(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova ativação</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="ID Computador (livre)"
              value={form.id_computador}
              onChange={(e) => setForm((f) => ({ ...f, id_computador: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Info (opcional)"
              value={form.info_complementar}
              onChange={(e) => setForm((f) => ({ ...f, info_complementar: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInserir(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarInserir} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
